import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchRetailerById, updateRetailer } from '../services/api.ts';
import { ArrowLeft, Upload, Store, Loader } from 'lucide-react';
import './EditRetailer.css';

const EditRetailer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    logo: '',
    domain: '',
    search_url: '',
    enable: true
  });

  useEffect(() => {
    const loadRetailer = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchRetailerById(id);
        setFormData({
          name: data.name || '',
          short_name: data.short_name || '',
          logo: data.logo || '',
          domain: data.domain || '',
          search_url: data.search_url || '',
          enable: data.enable || true
        });
        setLogoPreview(data.logo || '');
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load retailer');
        console.error('Error loading retailer:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRetailer();
  }, [id]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFormData({ ...formData, logo: url });
    setLogoPreview(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setError(null);
      await updateRetailer(id, formData);
      navigate('/dashboard/retailers');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to update retailer';
      setError(errorMessage);
      console.error('Update error:', err.response?.data);
    }
  };

  if (loading) {
    return (
      <div className="edit-retailer">
        <div className="loading-container">
          <Loader className="loading-spinner-icon" size={40} />
          <p>Loading retailer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-retailer">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/retailers')} title="Back to Retailers">
          <ArrowLeft size={20} />
        </button>
        <h2>Edit Retailer</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Retailer Information</h3>
            
            <div className="form-group">
              <label>Retailer Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter retailer name"
                required
              />
            </div>

            <div className="form-group">
              <label>Short Name *</label>
              <input
                type="text"
                value={formData.short_name}
                onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                placeholder="Enter short name (e.g., ABC)"
                required
              />
              <p className="field-hint">A brief identifier for the retailer</p>
            </div>

            <div className="form-group">
              <label>Logo URL *</label>
              <div className="input-with-icon">
                <Upload size={18} className="input-icon" />
                <input
                  type="text"
                  value={formData.logo}
                  onChange={handleLogoChange}
                  placeholder="https://example.com/logo.png"
                  required
                />
              </div>
              <p className="field-hint">Enter the URL of the retailer logo</p>
            </div>

            <div className="form-group">
              <label>Domain</label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                placeholder="e.g. walmart.com"
              />
              <p className="field-hint">The retailer's website domain (used for product search)</p>
            </div>

            <div className="form-group">
              <label>Search URL</label>
              <input
                type="text"
                value={formData.search_url}
                onChange={(e) => setFormData({ ...formData, search_url: e.target.value })}
                placeholder="https://www.example.com/search?q={query}"
              />
              <p className="field-hint">Direct search URL for this retailer. Use {'{query}'} as placeholder for the search term.</p>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Active Status</label>
                  <p className="switch-description">Enable this retailer for public display</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.enable}
                    onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              <div className="preview-logo-wrapper">
                {logoPreview ? (
                  <img 
                    src={logoPreview} 
                    alt="Logo Preview" 
                    className="preview-logo"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`preview-placeholder ${logoPreview ? 'hidden' : ''}`}>
                  <Store size={48} />
                  <p>Logo preview will appear here</p>
                </div>
              </div>
              
              <div className="preview-details">
                <h4>{formData.name || 'Retailer Name'}</h4>
                <p className="preview-short-name">{formData.short_name || 'SHORT_NAME'}</p>
                
                <div className="preview-badges">
                  <span className={`preview-badge ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>🏪 Retailer Guidelines</h4>
              <ul>
                <li>Use high-quality logo images (PNG/SVG preferred)</li>
                <li>Recommended size: 400x400px or larger</li>
                <li>Short name should be clear and recognizable</li>
                <li>Ensure logo has a transparent background</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/retailers')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRetailer;
